import { AppBar, Box, Breadcrumbs, Chip, Container, Link, Paper, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import React from 'react';
import { dataConstants } from '../../../constants';
import { roundNum } from '../../../helpers';
import { addCommas, compareFn, convertTblFromData, convertTickerTblFromData, formatValue, getFormatEntityN, orderTblByCol } from '../../../utils';
import PaidRoundedIcon from '@mui/icons-material/PaidRounded';
import cloneDeep from 'lodash.clonedeep';
import getPlotData from '../../../../lib/charts/_getPlotData';
import formulaUtil from '../../../helpers/formula';
import { convertDate } from '../../../helpers/time';
import eventObserver from '../../../utils/EventObserver';
import FundPortfolioValueChart from './FundPortfolioValueChart';
import NoData from '../../_common/NoData';
import dynamic from 'next/dynamic';
import LoadingItem from '../../_common/Skeleton/stock';
import ReplyIcon from '@mui/icons-material/Reply';
import Tooltip, { tooltipClasses } from '@mui/material/Tooltip';
import { isMobile } from '../../../helpers/deviceDetect';
import ShareThis from '../../_common/ShareThis';
import RecentFundNews from '../../Stock/RecentFundNews';
import StockIdeasAdvertisement from '../../StockIdeasAdvertisement';
import BuyAddRedSold from './BuyAddRedSold';
import Distribution from './Distribution';
import FundHeatMap from './FundHeatMap';
import Performance from './Performance';
import PerformanceDisplay from './Performance/TopMetric';
import WinnerAndLoser from './WinnerAndLoser';
import { colors, radii, shadows } from '../../../utils/designTokens';

const LightTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.arrow}`]: {
    color: '#2a2d64',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: '#2a2d64',
    fontWeight: 600,
    color: '#fff',
    boxShadow: theme.shadows[1],
    fontSize: 11,
    padding: '7px 15px 5px 15px',
  },
}));

const stockTickers = require('../../../../public/assets/search/stock_paths.json');

const CurrentHoldingsTable = dynamic(() => import('./CurrentHoldingsTable'),
  { loading: () => <LoadingItem height={500} /> });

const StyledBreadcrumb = styled(Chip)(({ theme }) => ({
  height: '24px',
  backgroundColor: 'transparent',
  fontWeight: theme.typography.fontWeightRegular,
  '&:hover, &:focus': {
    backgroundColor: theme.palette.background.primary_light,
    icon: {
      color: theme.palette.background.primary,
    }
  },
  '&:active': {
    boxShadow: theme.shadows[1],
  },
}));

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? colors.neutral[900] : colors.neutral[0],
  ...theme.typography.body2,
  height: '100%',
  color: theme.palette.text.secondary,
  boxShadow: theme.palette.mode === 'dark' ? 0 : shadows.xs,
  border: theme.palette.mode === 'dark' ? `1px solid ${colors.neutral[800]}` : `1px solid ${colors.neutral[200]}`,
  borderRadius: `${radii.lg}px`,
  transition: 'all 250ms cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    boxShadow: shadows.md,
    borderColor: colors.neutral[300],
  },
  '& .Item-Head': {
    background: theme.palette.mode === 'dark' ? colors.neutral[800] : colors.neutral[50],
    borderBottom: `1px solid ${colors.neutral[200]}`,
    marginBottom: 0,
    padding: theme.spacing(2, 2.5),
    borderRadius: `${radii.lg}px ${radii.lg}px 0 0`,
    '& a': {
      textDecoration: 'none',
    },
    '& .option-title': {
      fontWeight: 700,
      color: theme.palette.mode === 'dark' ? colors.neutral[0] : colors.neutral[900],
      fontSize: '1.05rem',
      letterSpacing: '-0.01em',
    }
  },
  '& .Item-Body': {
    padding: theme.spacing(2),
  }
}));

const { FUNDS_SECTIONS, SORT_DIR } = dataConstants || {};

const escapeHtml = (str) =>
  String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const escapeRegExp = (str) => String(str).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

class FundSummary extends React.PureComponent {
  state = {
    selHoldTicker: null,
    tickers: {},
    chartInit: [],
    sorting: {
      [FUNDS_SECTIONS.FUND_LATEST]: { colN: 1, dir: SORT_DIR.ASC },
      [FUNDS_SECTIONS.FUND_CHANGED]: { colN: 1, dir: SORT_DIR.ASC },
      [FUNDS_SECTIONS.FUND_BUYS]: { colN: 1, dir: SORT_DIR.ASC },
      [FUNDS_SECTIONS.FUND_SELLS]: { colN: 1, dir: SORT_DIR.ASC },
      [FUNDS_SECTIONS.FUND_HOLDINGS]: { colN: 1, dir: SORT_DIR.ASC },
    },
    selectedTblPop: null,
    orderBy: 'Name',
  };

  chartState = dataConstants.CHART_STATE.VIEW;
  _isMounted = false;

  componentDidMount() {
    this._isMounted = true;
    const { data } = this.props || {};
    const { sorting } = this.state || {};
    const nextSorting = cloneDeep(sorting);

    const findSection = (code) => (Array.isArray(data) ? data.find(d => d.section_code === code) : undefined);

    const holdings = findSection(FUNDS_SECTIONS.FUND_HOLDINGS);
    const latest = findSection(FUNDS_SECTIONS.FUND_LATEST);
    const changed = findSection(FUNDS_SECTIONS.FUND_CHANGED);
    const buys = findSection(FUNDS_SECTIONS.FUND_BUYS);
    const sells = findSection(FUNDS_SECTIONS.FUND_SELLS);

    if (latest?.settings?.sorting) nextSorting[FUNDS_SECTIONS.FUND_LATEST] = latest.settings.sorting;
    if (buys?.settings?.sorting) nextSorting[FUNDS_SECTIONS.FUND_BUYS] = buys.settings.sorting;
    if (sells?.settings?.sorting) nextSorting[FUNDS_SECTIONS.FUND_SELLS] = sells.settings.sorting;
    if (changed?.settings?.sorting) nextSorting[FUNDS_SECTIONS.FUND_CHANGED] = changed.settings.sorting;
    if (holdings?.settings?.sorting) nextSorting[FUNDS_SECTIONS.FUND_HOLDINGS] = holdings.settings.sorting;

    const holdingsTbl = holdings?.tabData?.length > 0 ? holdings.tabData : undefined;
    if (holdingsTbl && holdingsTbl.length > 0) {
      const holdingCols = holdings?.settings?.cols;
      const holdSortIdx = nextSorting[FUNDS_SECTIONS.FUND_HOLDINGS]?.colN;
      const hSortCol = holdSortIdx ? holdingCols?.[holdSortIdx - 1] : undefined;
      const hSort = hSortCol ? hSortCol.field : undefined;
      const sorted = hSort
        ? [...holdingsTbl].sort((a, b) => compareFn(a?.[hSort], b?.[hSort], nextSorting[FUNDS_SECTIONS.FUND_HOLDINGS].dir))
        : holdingsTbl;
      const index = sorted.findIndex(d => d?.ticker);
      if (index >= 0) this.requestChartData(sorted[index].ticker);
    }
    this.setState({ sorting: nextSorting });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  getChart = (chart, code, data, cik) => ({
    ...chart,
    series: [{ ...(chart?.series?.[0] || {}), yformula: code.replace('XXXXX', cik ?? this.props?.cik ?? '') }],
    ySeries: code,
    series_data: data,
  });

  getHoldCodes = (cik, selHoldTicker) => [
    `FUNDS${cik}.${selHoldTicker}.SHR`,
    `FUNDS${cik}.${selHoldTicker}.SHR|CHG`,
    `FUNDS${cik}.${selHoldTicker}.PORTPCT`,
  ];

  setSorting = (prop, val) => {
    this.setState(prevState => ({
      sorting: {
        ...prevState.sorting,
        [prop]: val,
      },
      orderBy: val?.name ?? prevState.orderBy,
    }));
  };

  requestChartData = (selHoldTicker) => {
    const { cik, data } = this.props || {};
    if (!selHoldTicker) {
      this.setState({ selHoldTicker: null });
      return;
    }
    this.setState({ selHoldTicker });

    const symbolCodes = this.getHoldCodes(cik, selHoldTicker);
    const findSection = (code) => (Array.isArray(data) ? data.find(d => d.section_code === code) : undefined);
    const fundShr = findSection(FUNDS_SECTIONS.FUND_SHR);
    const fundShrChg = findSection(FUNDS_SECTIONS.FUND_SHR_CHG);
    const fundPort = findSection(FUNDS_SECTIONS.FUND_PORT_PCT);
    const cached = this.state.tickers?.[selHoldTicker];

    const notifyRedraw = (section, code, seriesData) => {
      if (!section) return;
      const currentChartInit = this.state.chartInit || [];
      if (!currentChartInit.includes(section.section_code)) return;

      eventObserver.notify('reDraw', {
        chart: this.getChart({ ...section.tabData, id: section.section_code }, code, seriesData, cik),
        chartId: section.section_code,
        chartState: this.chartState,
      });
    };

    if (!cached) {
      getPlotData(symbolCodes)
        .then(d => {
          if (!this._isMounted) return;
          if (this.state.selHoldTicker !== selHoldTicker) return;

          const message = formulaUtil.validateSeriesCodes(symbolCodes, d, false);
          if (!message?.isValid) return;

          const plotData = {};
          symbolCodes.forEach((c) => {
            plotData[c] = d[c.replace(/[.@|$^:]/g, '_')] || [];
          });

          this.setState(prevState => ({
            tickers: { ...prevState.tickers, [selHoldTicker]: plotData },
          }), () => {
            notifyRedraw(fundShr, symbolCodes[0], plotData[symbolCodes[0]]);
            notifyRedraw(fundShrChg, symbolCodes[1], plotData[symbolCodes[1]]);
            notifyRedraw(fundPort, symbolCodes[2], plotData[symbolCodes[2]]);
          });
        })
        .catch(() => {});
    } else {
      notifyRedraw(fundShr, symbolCodes[0], cached[symbolCodes[0]]);
      notifyRedraw(fundShrChg, symbolCodes[1], cached[symbolCodes[1]]);
      notifyRedraw(fundPort, symbolCodes[2], cached[symbolCodes[2]]);
    }
  };

  setChartInit = (code) => {
    this.setState(prevState => {
      const chartInit = prevState.chartInit || [];
      if (chartInit.includes(code)) return null;
      return { chartInit: [...chartInit, code] };
    });
  };

  toggleTablePop = (selectedTblPop) => this.setState({ selectedTblPop });

  processTextWithLinks = (text) => {
    if (typeof text !== 'string') return '';
    const safe = escapeHtml(text);

    return safe.replace(/\(([^)]+)\)/g, (match, content) => {
      const trimmedContent = content.trim();
      const hasLink = Array.isArray(stockTickers) && stockTickers.length > 0
        ? stockTickers.find(e => e?.t === trimmedContent)
        : null;

      if (!isNaN(trimmedContent) || trimmedContent.length > 4) {
        return match;
      }
      return hasLink
        ? `(<a target="_blank" rel="noopener noreferrer" href="/stock/${encodeURIComponent(trimmedContent)}">${trimmedContent}</a>)`
        : match;
    });
  };

  render() {
    const { data, name, cik, themeMode, themes, fundNews, isPremium, openDialog, page_info, fund_metrics, posts, pageLoading, ad_settings, asPath } = this.props || {};
    const { selHoldTicker, tickers, sorting } = this.state || {};
    const { txtBlock, noStocks } = fund_metrics || {};

    let parsedManager;
    try { parsedManager = JSON.parse(txtBlock); } catch (error) { parsedManager = null; }
    const managerEntry = Array.isArray(parsedManager)
      ? parsedManager.find(d => d?.section === 'manager')
      : null;
    const manager = managerEntry?.text ? managerEntry.text : name;

    const findSection = (code) => (Array.isArray(data) ? data.find(d => d.section_code === code) : undefined);

    const fundChanged = findSection(FUNDS_SECTIONS.FUND_CHANGED);
    const fundChangedTbl = fundChanged?.tabData?.length > 0 ? convertTblFromData(fundChanged.tabData) : undefined;
    if (fundChangedTbl) fundChangedTbl.rows = orderTblByCol([...fundChangedTbl.rows], sorting[FUNDS_SECTIONS.FUND_CHANGED]);

    const holdings = findSection(FUNDS_SECTIONS.FUND_HOLDINGS);
    let holdingsTbl = holdings?.tabData?.length > 0 ? holdings.tabData : undefined;
    holdingsTbl = holdingsTbl && holdingsTbl.length > 0
      ? holdingsTbl.filter(type => type.putCall !== 'PUT' && type.putCall !== 'CALL')
      : null;

    const holdingCols = holdings?.settings?.cols;
    const holdSortIdx = sorting[FUNDS_SECTIONS.FUND_HOLDINGS]?.colN;
    const holdSortCol = holdSortIdx ? holdingCols?.[holdSortIdx - 1] : undefined;
    const holdSortProp = holdSortCol ? holdSortCol.field : undefined;
    const addTable = holdings?.settings?.addTable;

    holdingsTbl = holdingsTbl && holdSortProp
      ? [...holdingsTbl].sort((a, b) => compareFn(a?.[holdSortProp], b?.[holdSortProp], sorting[FUNDS_SECTIONS.FUND_HOLDINGS].dir))
      : holdingsTbl;

    const fundLatest = findSection(FUNDS_SECTIONS.FUND_LATEST);
    const fundLatestTbl = fundLatest?.tabData?.length > 0 ? convertTblFromData(fundLatest.tabData) : undefined;
    if (fundLatestTbl) fundLatestTbl.rows = orderTblByCol([...fundLatestTbl.rows], sorting[FUNDS_SECTIONS.FUND_LATEST]);

    const symbolCodes = selHoldTicker ? this.getHoldCodes(cik, selHoldTicker) : undefined;

    const fundPortVal = findSection(FUNDS_SECTIONS.FUND_PORT_C);
    let fundPortValC = fundPortVal?.tabData;
    let fundSeries = findSection(FUNDS_SECTIONS.FUND_MAIN_SERIES);
    fundSeries = fundSeries?.tabData;
    fundPortValC = fundPortValC && fundSeries ? this.getChart(fundPortValC, 'FUNDSXXXXX.PORTVAL', fundSeries, cik) : undefined;
    const portValLen = fundPortValC?.series_data?.length ?? 0;
    const hasPortVal = portValLen > 0;

    const entity = fundSeries?.length > 0 ? getFormatEntityN(fundSeries[0].value, true) : undefined;

    const top_text = Array.isArray(page_info) && page_info.length > 0
      ? page_info.find(d => d.section_code === 'TOP')
      : undefined;

    const hasData = !!(name && Array.isArray(data) && data.length > 0);

    const topTextHtml = top_text?.answer
      ? this.processTextWithLinks(top_text.answer)?.replace(new RegExp(escapeRegExp(name ?? ''), 'g'), manager)
      : '';

    return (
      <NoData noData={!hasData}>
        <div className="fund-tab-body sum-section" style={{ width: '100%', overflow: 'hidden' }}>
          <Box component="div" className="row sum-r" sx={{ m: 0 }}>
            <Box component="div" className="col-md-12">
              <Box component="div" className="fund-latest">
                <Box sx={{ flexGrow: 1, mb: 1 }}>
                  <AppBar position="static" sx={{ px: 1, pb: fundSeries?.length > 0 ? 0 : 1.5, pt: 3, borderRadius: '5px', top: 0, bgcolor: themeMode === 'dark' ? 'background.default' : '#f8f9fa', boxShadow: '0 0.5px 1px rgb(132 135 154 / 20%), 0 2px 3px rgb(132 135 154 / 10%)' }} elevation={0}>
                    <Container maxWidth="xl" sx={{ mb: fundSeries?.length > 0 ? 1 : 0 }}>
                      <Box component="div" sx={{ display: 'flex', flexDirection: { md: 'row', sm: 'column', xs: 'column' }, justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box component="div" className="fund-info p-0">
                          <Box
                            sx={{
                              display: 'flex', alignItems: 'end',
                              '& .hyper-link': { display: 'none', fontSize: '1.1rem' }, '&:hover .hyper-link': { display: 'inline-block' },
                              '&:hover .hyper-share': { display: 'none' },
                              '&:hover .fund-name': { borderBottom: '1px dotted' }, '& .hyper-link:hover ': { color: '#174c86', cursor: 'pointer' },
                              '.fund-name': { borderBottom: '1px solid transparent' },
                            }}>
                            <Typography className="fund-name" variant="h1" sx={{ color: '#222', fontSize: '1.5rem', fontWeight: 600, mr: 1 }}>
                              {`Latest ${name ?? ''} Stock Portfolio`}
                            </Typography>
                          </Box>
                          {fundSeries?.length > 0 &&
                            <Typography variant="subtitle1" sx={{ color: 'text.link', fontSize: '2.25rem', fontWeight: 600, mt: 0, lineHeight: 1.25 }}>
                              <Box component="span" sx={{ color: 'text.link', fontSize: '1.2rem', fontWeight: 600, verticalAlign: 'middle', mr: .2 }}>$</Box>
                              {addCommas(roundNum(formatValue(fundSeries[0].value, entity, true)))}
                              <Box component="span" sx={{ color: 'text.link', fontSize: '1.2rem', fontWeight: 600, ml: 0.5 }}>{entity}</Box>
                              {noStocks && <Box component="span" sx={{ color: '#555', fontSize: '.9rem', fontWeight: 500, ml: .5, mb: .3 }}> | No. of Holdings <b>#{noStocks}</b></Box>}
                            </Typography>}
                        </Box>
                        <PerformanceDisplay
                          txtBlock={txtBlock}
                          manager={manager}
                        />
                      </Box>
                    </Container>
                  </AppBar>
                </Box>

                <Container maxWidth="xl">
                  <Box component="nav"
                    className="bread-container"
                    aria-label="breadcrumb"
                    sx={{ my: 1, padding: '8px', borderRadius: '5px' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Breadcrumbs>
                        <Link color="inherit" href={`${dataConstants.NAVS.FUNDS}`}>
                          <StyledBreadcrumb
                            component="div"
                            label="Funds"
                            icon={<PaidRoundedIcon sx={{ '&.MuiChip-icon': { color: 'text.very_light_desc', fontSize: '1rem' }, color: 'text.link' }} fontSize="small" />}
                            sx={{ color: 'text.very_light_desc', fontSize: '.78rem', cursor: 'pointer' }}
                          />
                        </Link>
                        <StyledBreadcrumb
                          label={`${cik ?? ''}`}
                          className={'bread-bg-blue'}
                          sx={{ backgroundColor: '#f2f2f2', fontSize: '.78rem' }}
                        />
                      </Breadcrumbs>
                      <ShareThis
                        link={`${process.env.NEXT_PUBLIC_URL}${asPath ?? ''}`}
                        title={`View ${name ?? ''} on Grufity`}
                        showButton={true}
                        tooltipText={`Share this page`}
                        showThisButton={<ReplyIcon fontSize="inherit" sx={{ transform: 'rotateY(180deg)' }} />}
                        ticker={cik}
                      />
                    </Box>
                  </Box>
                </Container>

                <Container maxWidth="xl">
                  <Box component="div">
                    <Grid container spacing={1.2}>
                      {top_text?.answer &&
                        <Grid size={{ xs: 12, md: !hasPortVal ? 6 : 4 }} sx={{ height: 'auto', '& a': { color: '#174ea6' } }}>
                          <Item component="div" className="fund-port-val p-2">
                            <Box component="div" className="Item-Head">
                              <Typography className="option-title" variant="h2" gutterBottom sx={{ fontSize: '1rem !important' }}>
                                {`About XXXXX and 13F Hedge Fund Stock Holdings`.replace(/XXXXX/g, manager)}
                              </Typography>
                            </Box>
                            <Box component="div" className="option-content" sx={{ m: 2 }}>
                              <Typography
                                component="div"
                                sx={{ fontSize: '.9rem' }}
                                dangerouslySetInnerHTML={{ __html: topTextHtml }}
                              />
                            </Box>
                          </Item>
                        </Grid>}
                      {hasPortVal ?
                        <Grid size={{ xs: 12, md: top_text?.answer ? 4 : 3 }}>
                          <Item component="div" className="fund-port-val p-2">
                            <Box component="div" className="Item-Head">
                              <Typography className="option-title" variant="body1" gutterBottom>
                                {manager} {fundPortVal?.section_name}
                              </Typography>
                              {fundSeries?.length > 0 && holdingsTbl?.length > 0 && holdingsTbl[0]?.dateFiled &&
                                <Box component="div" className="company-description align-items-end">
                                  <Typography
                                    component="div"
                                    variant="caption"
                                    gutterBottom
                                    sx={{ fontSize: '.8rem', color: 'text.very_light_desc' }}>
                                    Last Reported on: {convertDate(holdingsTbl[0]?.dateFiled, 'dd MMM, yyyy')}
                                  </Typography>
                                </Box>}
                            </Box>
                            <Box component="div" className="option-content portfolio-char" sx={{ my: 2, px: 2 }}>
                              <FundPortfolioValueChart
                                seriesData={fundPortValC?.series_data}
                                seriesStyle={fundPortValC?.series?.[0]?.setting}
                                height={220}
                                title={`${manager} ${fundPortVal?.section_name ?? 'Portfolio Value'}`}
                                latestDate={holdingsTbl?.[0]?.dateFiled}
                              />
                            </Box>
                          </Item>
                        </Grid> : null}

                      <Performance
                        fund_metrics={fund_metrics}
                        col6={!hasPortVal}
                        name={manager}
                      />
                      <BuyAddRedSold
                        addTable={addTable}
                        holdingsTbl={holdingsTbl}
                        top_text={top_text}
                        stockTickers={stockTickers}
                        name={name}
                      />
                    </Grid>
                  </Box>
                </Container>
              </Box>
            </Box>
          </Box>

          {posts?.length > 0 ?
            <Container maxWidth="xl" sx={{ py: '1px' }}>
              <StockIdeasAdvertisement
                stock_ideas={posts}
                positionBottom={true}
                pageLoading={pageLoading}
                ad_settings={ad_settings}
                tab="fund"
              />
            </Container> : null}
            
          <Distribution fund_metrics={fund_metrics} />
          <WinnerAndLoser fund_metrics={fund_metrics} />

          <Container maxWidth="xl">
            <FundHeatMap
              data={data}
              stockTickers={stockTickers}
              name={manager}
              heatmapSettings={this.props.heatmapSettings}
            />
            <Box component="div" className="row sum-r f-hold-r" sx={{ mt: 1.2 }}>
              <CurrentHoldingsTable
                data={data}
                sorting={sorting}
                setSorting={this.setSorting}
                state={this.state}
                requestChartData={this.requestChartData}
                setChartInit={this.setChartInit}
                chartState={this.chartState}
                themeMode={themeMode}
                themes={themes}
                name={manager}
                tickers={tickers}
                selHoldTicker={selHoldTicker}
                symbolCodes={symbolCodes}
                stockTickers={stockTickers}
                isPremium={isPremium}
                showPricingBanner={openDialog}
                openDialog={openDialog}
                cik={cik}
                txtBlock={txtBlock}
              />
            </Box>
          </Container>

          <Container maxWidth="xl">
            <RecentFundNews
              news={fundNews}
              title="Recent News"
              columns={isMobile ? 1 : 2}
            />
          </Container>
        </div>
      </NoData>
    );
  }
}

export default FundSummary;